import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useData } from '../context/DataContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiX, FiSend, FiUser, FiChevronRight, FiArrowLeft, FiStar, FiCalendar, FiBookOpen } = FiIcons;

const ReviewModal = ({ student, session = null, onClose }) => {
  const { addTutorReview, getParentById, sessions } = useData();
  const [view, setView] = useState(session ? 'form' : 'list');
  const [selectedTutor, setSelectedTutor] = useState(session ? { name: session.tutor, subject: session.subject } : null);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  
  const parent = getParentById(student.parentId);

  // Robust Tutor Discovery: Merge Assigned Subjects + Actual Session History
  const availableTutors = useMemo(() => {
    const tutorsMap = new Map();

    // 1. Check assigned subjects in student profile
    if (student.subjects && Array.isArray(student.subjects)) {
      student.subjects.forEach(sub => {
        if (sub.tutor) {
          const current = tutorsMap.get(sub.tutor) || new Set();
          current.add(sub.subject);
          tutorsMap.set(sub.tutor, current);
        }
      });
    }

    // 2. Check historical sessions for any other tutors (subs, etc.)
    const studentSessions = sessions[student.id] || {};
    Object.values(studentSessions).flat().forEach(s => {
      if (s.tutor) {
        const current = tutorsMap.get(s.tutor) || new Set();
        current.add(s.subject);
        tutorsMap.set(s.tutor, current);
      }
    });

    return Array.from(tutorsMap.entries()).map(([name, subs]) => ({
      name,
      subjects: Array.from(subs)
    }));
  }, [student, sessions]);

  const handleSelectTutor = (tutor) => {
    setSelectedTutor(tutor);
    setView('form');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!comment.trim()) return;
    
    setSubmitting(true);
    try {
      await addTutorReview({
        tutorName: selectedTutor.name,
        subject: selectedTutor.subject || (selectedTutor.subjects ? selectedTutor.subjects.join(', ') : 'General'),
        studentId: student.id,
        studentName: student.studentName,
        sessionId: session?.id || null, // Link to specific session if applicable
        sessionDate: session?.date || null,
        parentId: student.parentId,
        parentName: parent ? `${parent.firstName} ${parent.lastName}` : 'Anonymous Parent',
        comment: comment.trim(),
        createdAt: new Date().toISOString()
      });
      
      alert(`Feedback for ${selectedTutor.name} has been recorded.`);
      setComment('');
      if (session) {
        onClose(); // Close immediately if it was a session-specific rating
      } else {
        setView('list'); // Return to list if it was a general rating
        setSelectedTutor(null);
      }
    } catch (error) {
      alert('Failed to submit feedback.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-4 z-[100] backdrop-blur-md">
      <motion.div 
        initial={{ opacity: 0, scale: 0.95, y: 20 }} 
        animate={{ opacity: 1, scale: 1, y: 0 }} 
        className="bg-white rounded-[3rem] shadow-2xl max-w-lg w-full overflow-hidden border border-white/20"
      >
        <div className="p-8 border-b border-gray-100 flex items-center justify-between bg-gray-50/50">
          <div className="flex items-center gap-4">
            {view === 'form' && !session && (
              <button 
                onClick={() => setView('list')}
                className="w-10 h-10 rounded-full bg-white shadow-sm flex items-center justify-center text-gray-400 hover:text-indigo-600 transition-all"
              >
                <SafeIcon icon={FiArrowLeft} />
              </button>
            )}
            <div>
              <h2 className="text-2xl font-black text-gray-900 tracking-tight uppercase">
                {session ? 'Session Feedback' : view === 'list' ? 'Rate Tutors' : 'Write Review'}
              </h2>
              <p className="text-[10px] font-black text-indigo-500 uppercase tracking-[0.2em]">
                For {student.studentName}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 text-gray-300 hover:text-gray-900 transition-colors">
            <SafeIcon icon={FiX} className="text-2xl" />
          </button>
        </div>

        <div className="p-8">
          <AnimatePresence mode="wait">
            {view === 'list' ? (
              <motion.div 
                key="list"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                className="space-y-4"
              >
                <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-6 px-1">
                  Select an instructor to provide overall feedback:
                </p>
                <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2 no-scrollbar">
                  {availableTutors.map((tutor, idx) => (
                    <button
                      key={idx}
                      onClick={() => handleSelectTutor(tutor)}
                      className="w-full p-6 bg-gray-50 hover:bg-indigo-50 border-2 border-transparent hover:border-indigo-100 rounded-[2rem] transition-all flex items-center justify-between group"
                    >
                      <div className="flex items-center gap-4 text-left">
                        <div className="w-14 h-14 rounded-2xl bg-white shadow-sm flex items-center justify-center text-indigo-600 group-hover:bg-indigo-600 group-hover:text-white transition-all">
                          <SafeIcon icon={FiUser} className="text-xl" />
                        </div>
                        <div>
                          <h4 className="font-black text-gray-900 text-lg">{tutor.name}</h4>
                          <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                            {tutor.subjects.join(' • ')}
                          </p>
                        </div>
                      </div>
                      <SafeIcon icon={FiChevronRight} className="text-gray-300 group-hover:text-indigo-600 group-hover:translate-x-1 transition-all" />
                    </button>
                  ))}
                </div>
              </motion.div>
            ) : (
              <motion.form 
                key="form"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                onSubmit={handleSubmit}
                className="space-y-6"
              >
                <div className="bg-indigo-600 rounded-[2rem] p-6 text-white shadow-xl shadow-indigo-100">
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                      <SafeIcon icon={FiStar} />
                    </div>
                    {session && (
                      <div className="text-right">
                        <div className="flex items-center gap-2 text-[9px] font-black uppercase tracking-widest text-indigo-200">
                          <SafeIcon icon={FiCalendar} /> {new Date(session.date).toLocaleDateString()}
                        </div>
                        <div className="flex items-center gap-2 text-[9px] font-black uppercase tracking-widest text-indigo-200 mt-1">
                          <SafeIcon icon={FiBookOpen} /> {session.subject}
                        </div>
                      </div>
                    )}
                  </div>
                  <div>
                    <p className="text-[10px] font-black uppercase tracking-widest text-indigo-200">Reviewing</p>
                    <h3 className="text-2xl font-black">{selectedTutor?.name}</h3>
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Your Observations</label>
                  <textarea 
                    autoFocus
                    value={comment} 
                    onChange={(e) => setComment(e.target.value)} 
                    rows={6} 
                    className="w-full p-6 bg-gray-50 border-2 border-transparent rounded-[2rem] focus:bg-white focus:border-indigo-600 outline-none font-bold text-gray-700 transition-all placeholder:text-gray-300" 
                    placeholder={session 
                      ? `How was the class on ${new Date(session.date).toLocaleDateString()}?` 
                      : `How is ${selectedTutor?.name} doing overall?`
                    } 
                    required 
                  />
                </div>

                <button 
                  type="submit" 
                  disabled={submitting || !comment.trim()} 
                  className="w-full py-5 bg-gray-900 text-white rounded-2xl font-black text-xs uppercase tracking-[0.2em] shadow-xl hover:bg-black transition-all disabled:opacity-30 flex items-center justify-center gap-3"
                >
                  {submitting ? 'Processing...' : <><SafeIcon icon={FiSend} /> Submit Feedback</>}
                </button>
              </motion.form>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </div>
  );
};

export default ReviewModal;